import { Controller } from "@hotwired/stimulus"

const TOKEN_PATTERN = /@\[([^\]]{1,120})\]\((person|work_stream):([0-9a-fA-F-]{36})\)/g

export default class extends Controller {
  static targets = ["editor", "hidden", "menu", "send"]
  static values = { options: Array, people: Array }

  connect() {
    this.activeIndex = 0
    this.matches = []
    this.submitting = false
    this.optionLookup = this.buildOptionLookup()
    this.renderInitialValue()
    this.syncHidden()
  }

  input() {
    this.normalizeEmptyEditor()
    this.syncHidden()
    this.renderMenu()
  }

  keydown(event) {
    if (!this.hasMenuTarget || this.menuTarget.hidden) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.activeIndex = Math.min(this.activeIndex + 1, this.matches.length - 1)
      this.renderItems()
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.activeIndex = Math.max(this.activeIndex - 1, 0)
      this.renderItems()
    } else if (event.key === "Enter" || event.key === "Tab") {
      if (this.matches[this.activeIndex]) {
        event.preventDefault()
        this.chooseOption(this.matches[this.activeIndex])
      }
    } else if (event.key === "Escape") {
      this.closeMenu()
    }
  }

  paste(event) {
    event.preventDefault()
    this.insertTextAtSelection(event.clipboardData.getData("text/plain"))
    this.input()
  }

  submit(event) {
    if (this.submitting) {
      event.preventDefault()
      return
    }

    this.syncHidden()

    if (this.hiddenTarget.value.trim() === "") {
      event.preventDefault()
      this.editorTarget.focus()
      return
    }

    this.submitting = true
    this.element.setAttribute("aria-busy", "true")
    if (this.hasSendTarget) this.sendTarget.disabled = true
  }

  blur() {
    window.setTimeout(() => this.closeMenu(), 150)
  }

  select(event) {
    event.preventDefault()
    const option = this.optionLookup.get(event.currentTarget.dataset.mentionKey)
    if (option) this.chooseOption(option)
  }

  renderMenu() {
    if (!this.hasMenuTarget) return

    const token = this.currentToken()
    if (!token) {
      this.closeMenu()
      return
    }

    const query = token.query.toLowerCase()
    this.matches = this.options
      .filter((option) => {
        const haystack = `${option.name || ""} ${option.subtitle || ""}`.toLowerCase()
        return haystack.includes(query)
      })
      .slice(0, 8)

    if (this.matches.length === 0) {
      this.closeMenu()
      return
    }

    this.activeIndex = 0
    this.menuTarget.hidden = false
    this.renderItems()
  }

  renderItems() {
    this.menuTarget.innerHTML = this.matches.map((option, index) => {
      const activeClass = index === this.activeIndex ? " mention-editor__option--active" : ""
      const key = this.optionKey(option)
      const subtitle = option.subtitle ? `<span class="mention-editor__option-subtitle">${this.escapeHtml(option.subtitle)}</span>` : ""
      return `
        <button type="button"
                class="mention-editor__option${activeClass}"
                data-action="mousedown->${this.identifier}#select"
                data-mention-key="${this.escapeHtml(key)}">
          <span class="mention-editor__option-label">${this.escapeHtml(option.name)}</span>
          ${subtitle}
        </button>
      `
    }).join("")
  }

  chooseOption(option) {
    const token = this.currentToken()
    if (!token) return

    const range = document.createRange()
    range.setStart(token.node, token.startOffset)
    range.setEnd(token.node, token.endOffset)
    range.deleteContents()

    const mention = this.buildMention(option)
    const spacer = document.createTextNode(" ")
    const fragment = document.createDocumentFragment()
    fragment.appendChild(mention)
    fragment.appendChild(spacer)

    range.insertNode(fragment)

    this.editorTarget.focus()
    this.placeCaret(spacer, spacer.textContent.length)
    this.syncHidden()
    this.closeMenu()
  }

  currentToken() {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) return null

    const range = selection.getRangeAt(0)
    if (!this.editorTarget.contains(range.startContainer)) return null
    if (range.startContainer.nodeType !== Node.TEXT_NODE) return null

    const prefix = range.startContainer.textContent.slice(0, range.startOffset)
    const match = prefix.match(/(^|\s)@([^\s@]{0,40})$/)
    if (!match) return null

    return {
      node: range.startContainer,
      startOffset: range.startOffset - match[2].length - 1,
      endOffset: range.startOffset,
      query: match[2]
    }
  }

  closeMenu() {
    if (!this.hasMenuTarget) return

    this.menuTarget.hidden = true
    this.matches = []
  }

  buildMention(option) {
    const mention = document.createElement("span")
    mention.className = "mention-editor__mention"
    mention.dataset.mentionType = option.type
    mention.dataset.mentionId = option.id
    mention.dataset.mentionName = option.name
    mention.contentEditable = "false"
    mention.textContent = `@${option.name}`
    return mention
  }

  insertTextAtSelection(text) {
    const range = this.currentRange()
    if (!range) return

    range.deleteContents()
    const textNode = document.createTextNode(text.replace(/\r\n?/g, "\n"))
    range.insertNode(textNode)
    this.placeCaret(textNode, textNode.textContent.length)
  }

  currentRange() {
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      if (this.editorTarget.contains(range.commonAncestorContainer)) return range
    }

    const range = document.createRange()
    range.selectNodeContents(this.editorTarget)
    range.collapse(false)
    return range
  }

  placeCaret(node, offset) {
    const range = document.createRange()
    const selection = window.getSelection()
    range.setStart(node, offset)
    range.collapse(true)
    selection.removeAllRanges()
    selection.addRange(range)
  }

  syncHidden() {
    this.hiddenTarget.value = this.serializeNodes(this.editorTarget.childNodes)
  }

  serializeNodes(nodes) {
    return Array.from(nodes).map((node) => this.serializeNode(node)).join("")
  }

  serializeNode(node) {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent || ""
    if (node.nodeType !== Node.ELEMENT_NODE) return ""

    if (node.dataset.mentionId) {
      return `@[${node.dataset.mentionName}](${node.dataset.mentionType}:${node.dataset.mentionId})`
    }

    if (node.tagName === "BR") return "\n"

    const serialized = this.serializeNodes(node.childNodes)
    if (node.tagName === "DIV" || node.tagName === "P") return `${serialized}\n`

    return serialized
  }

  normalizeEmptyEditor() {
    if (this.editorTarget.textContent === "" && this.editorTarget.querySelectorAll(".mention-editor__mention").length === 0) {
      this.editorTarget.innerHTML = ""
    }
  }

  renderInitialValue() {
    if (!this.hasHiddenTarget || !this.hasEditorTarget) return
    if (this.editorTarget.childNodes.length > 0) return

    const value = this.hiddenTarget.value || ""
    if (value.trim() === "") return

    let cursor = 0
    let match
    TOKEN_PATTERN.lastIndex = 0

    while ((match = TOKEN_PATTERN.exec(value)) !== null) {
      if (match.index > cursor) {
        this.editorTarget.appendChild(document.createTextNode(value.slice(cursor, match.index)))
      }

      const option = this.optionLookup.get(`${match[2]}:${match[3]}`) || {
        type: match[2],
        id: match[3],
        name: match[1]
      }
      this.editorTarget.appendChild(this.buildMention(option))
      cursor = match.index + match[0].length
    }

    if (cursor < value.length) {
      this.editorTarget.appendChild(document.createTextNode(value.slice(cursor)))
    }
  }

  get options() {
    if (this.hasOptionsValue && this.optionsValue.length > 0) return this.optionsValue

    return (this.hasPeopleValue ? this.peopleValue : []).map((person) => ({
      type: "person",
      id: person.id,
      name: person.name,
      group: person.group || "People",
      subtitle: person.subtitle
    }))
  }

  buildOptionLookup() {
    return new Map(this.options.map((option) => [this.optionKey(option), option]))
  }

  optionKey(option) {
    return `${option.type}:${option.id}`
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text || ""
    return div.innerHTML
  }
}
